import { useEffect, useState } from 'react';
import { api, googleAuthUrl, type Profile } from '../../api/client';
import { useLocale } from '../../i18n/LocaleContext';
import { useAuth } from '../AuthContext';

export function ProfilePage() {
  const { user, ensureGuest, setUsername, refresh } = useAuth();
  const { t } = useLocale();
  const [name, setName] = useState('');
  const [history, setHistory] = useState<Awaited<ReturnType<typeof api.history>>['rows']>([]);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [google, setGoogle] = useState(false);

  useEffect(() => {
    void ensureGuest().then(() => refresh());
    void api.providers().then((p) => setGoogle(p.google));
  }, [ensureGuest, refresh]);

  useEffect(() => {
    if (!user) return;
    void api
      .history(1)
      .then((h) => setHistory(h.rows))
      .catch(() => undefined);
  }, [user]);

  const saveName = async () => {
    setError(null);
    setOk(null);
    try {
      await setUsername(name.trim());
      setName('');
      setOk(t('profileNameSaved'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  if (!user) return <section className="panel">{t('loading')}</section>;

  return (
    <section className="panel">
      <h1>{t('profileTitle')}</h1>
      <p className="muted">
        {user.username
          ? t('profilePlayingAs', { name: user.username })
          : t('nameGateHint')}
      </p>
      <div className="field-row">
        <input
          placeholder={t('profileUsername')}
          value={name}
          maxLength={16}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void saveName();
          }}
        />
        <button type="button" className="btn primary" onClick={() => void saveName()}>
          {user.username ? t('profileChangeName') : t('profileSave')}
        </button>
      </div>
      {error ? <p className="error">{error}</p> : null}
      {ok ? <p className="muted">{ok}</p> : null}
      <Stats user={user} />
      {google && user.isGuest ? (
        <p>
          <a className="btn" href={googleAuthUrl()}>
            {t('profileLinkGoogle')}
          </a>
        </p>
      ) : null}
      <h2>{t('profileRecent')}</h2>
      <ul className="history-list">
        {history.map((r) => (
          <li key={r.id}>
            <strong>{r.mode}</strong> · {t('soloScore').toLowerCase()} {r.score} ·{' '}
            {t('soloStreak').toLowerCase()} {r.bestStreak} · {new Date(r.date).toLocaleString()}
            {r.ranked ? ` · Δ${r.ratingDelta}` : ''}
          </li>
        ))}
        {history.length === 0 ? <li className="muted">{t('profileNoRuns')}</li> : null}
      </ul>
    </section>
  );
}

function Stats({ user }: { user: Profile }) {
  const { t } = useLocale();
  return (
    <ul className="stat-list">
      <li>
        {t('profileRating')}: {user.rating} ({t('profilePeak')} {user.highestRating})
      </li>
      <li>
        {t('profileQuizGames')}: {user.quizGamesPlayed}
      </li>
      <li>
        {t('profileBestTimed')}: {user.bestTimedScore}
      </li>
      <li>
        {t('profileBestStreak')}: {user.bestStreak}
      </li>
      <li>
        {t('profileAnswers')}: {user.correctAnswers} {t('profileCorrect')} / {user.wrongAnswers}{' '}
        {t('profileWrong')}
      </li>
      <li>
        {t('profileRanked')}: {user.wins}-{user.losses}-{user.draws}
      </li>
    </ul>
  );
}
